// CustomTrailDetailPage — Map-Ansicht eines gespeicherten Custom-Trails.
// =======================================================================
//
// Wave 14bv Stage 6j: lädt den Custom-Trail aus IDB, rendert eine Mini-
// Map mit der Route drauf, Start-/End-Marker, Länge + Datum im Top-
// Banner. Zoom-Buttons wie in CreateCustomPage.
//
// Scope bewusst schmal: der User hat die Route gerade selbst erstellt,
// er braucht hauptsächlich sofortige visuelle Bestätigung + "so komme ich
// auf diese Route zurück wann immer ich will". Richtige "Ride"-Integration
// (wie bei normalen Trails via MapPage Selection-Sheet) kommt wenn nötig —
// für MVP reicht ein eigener Detail-Screen.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import {
  buildLiguriaStyle,
  buildMapoutStyle,
} from '../lib/map/style';
import {
  addPmtilesArchive,
  registerPmtilesProtocol,
} from '../lib/map/pmtiles';
import { opfsStatus } from '../lib/opfsStore';
import { tilesManifest } from '../lib/api';
import { LIGURIA_CENTER } from '@adtu/shared';
import {
  getCustomTrail,
  renameCustomTrail,
  setCustomTrailDifficulty,
  setCustomTrailShowOnMap,
  type CustomTrail,
} from '../lib/customTrails';
import { PredictBadge } from '../components/PredictBadge';
import {
  DIFFICULTY_LABEL,
  DIFFICULTY_ORDER,
  difficultySwatchBackground,
  type Difficulty,
} from '@adtu/shared';

const ROUTE_SOURCE = 'adt-custom-detail-route';
const ROUTE_LAYER = 'adt-custom-detail-route-line';
const ROUTE_CASING_LAYER = 'adt-custom-detail-route-casing';

function formatKm(m: number): string {
  return `${(m / 1000).toFixed(1)} km`;
}

export function CustomTrailDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [trail, setTrail] = useState<CustomTrail | null>(null);
  const [notFound, setNotFound] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [styleVersion, setStyleVersion] = useState(0);
  const startMarkerRef = useRef<maplibregl.Marker | null>(null);
  const endMarkerRef = useRef<maplibregl.Marker | null>(null);

  // Wave 14cf-fix15: Edit-Sheet state. Bei `open` rendert ein Modal mit
  // Name-Input + Difficulty-Picker (Difficulty-Skala wie bei Trails).
  const [editOpen, setEditOpen] = useState(false);

  // Load trail aus IDB.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getCustomTrail(decodeURIComponent(id))
      .then((t) => {
        if (cancelled) return;
        if (!t) setNotFound(true);
        else setTrail(t);
      })
      .catch((err) => {
        console.warn('[customTrailDetail] load failed', err);
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Map init — gleiche PMTiles-Strategie wie CreateCustomPage.
  useEffect(() => {
    if (!containerRef.current) return;
    registerPmtilesProtocol();

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildMapoutStyle(),
      center: LIGURIA_CENTER,
      zoom: 12,
      attributionControl: { compact: true },
    });
    mapRef.current = map;

    let styleSwapAborted = false;
    (async () => {
      try {
        const manifest = await tilesManifest();
        if (styleSwapAborted) return;
        const skipPmtiles = new URLSearchParams(location.search).has('nopmtiles');
        if (skipPmtiles || !manifest.map) return;

        const mapUrl = `pmtiles://${location.origin}${manifest.map.url}`;
        const contourUrl = manifest.contours
          ? `pmtiles://${location.origin}${manifest.contours.url}`
          : undefined;
        const hillshadeAvailable =
          manifest.hillshade
            ? (await opfsStatus(`${location.origin}${manifest.hillshade.url}`))
                .exists
            : false;
        if (styleSwapAborted) return;
        const hillshadeUrl =
          hillshadeAvailable && manifest.hillshade
            ? `pmtiles://${location.origin}${manifest.hillshade.url}`
            : undefined;

        addPmtilesArchive(mapUrl.slice('pmtiles://'.length));
        if (contourUrl) addPmtilesArchive(contourUrl.slice('pmtiles://'.length));
        if (hillshadeUrl) addPmtilesArchive(hillshadeUrl.slice('pmtiles://'.length));

        const rich = buildLiguriaStyle({
          mapPmtilesUrl: mapUrl,
          contourPmtilesUrl: contourUrl,
          hillshade: !!hillshadeUrl,
          hillshadePmtilesUrl: hillshadeUrl,
        });
        map.setStyle(rich, { diff: false });
      } catch (err) {
        console.warn('[customTrailDetail] style upgrade failed', err);
      }
    })();

    map.on('style.load', () => {
      if (!map.getSource(ROUTE_SOURCE)) {
        map.addSource(ROUTE_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }
      if (!map.getLayer(ROUTE_CASING_LAYER)) {
        map.addLayer({
          id: ROUTE_CASING_LAYER,
          type: 'line',
          source: ROUTE_SOURCE,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#0c4a6e',
            'line-width': 9,
            'line-opacity': 0.75,
          },
        });
      }
      if (!map.getLayer(ROUTE_LAYER)) {
        map.addLayer({
          id: ROUTE_LAYER,
          type: 'line',
          source: ROUTE_SOURCE,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#38bdf8',
            'line-width': 5,
            'line-opacity': 1,
          },
        });
      }
      setMapReady(true);
      setStyleVersion((v) => v + 1);
    });

    return () => {
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
      if (endMarkerRef.current) {
        endMarkerRef.current.remove();
        endMarkerRef.current = null;
      }
      styleSwapAborted = true;
      mapRef.current = null;
      setMapReady(false);
      map.remove();
    };
  }, []);

  // Trail-Coords in die Map einspeisen sobald beides da ist.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !trail) return;
    const src = map.getSource(ROUTE_SOURCE) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!src) return;
    if (trail.coords.length < 2) return;
    src.setData({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: trail.coords },
    });

    // Start/End-Marker.
    if (startMarkerRef.current) startMarkerRef.current.remove();
    if (endMarkerRef.current) endMarkerRef.current.remove();
    const [start, end] = [
      trail.coords[0],
      trail.coords[trail.coords.length - 1],
    ];
    startMarkerRef.current = new maplibregl.Marker({
      element: makePinElement('start'),
      anchor: 'bottom',
    })
      .setLngLat(start as [number, number])
      .addTo(map);
    endMarkerRef.current = new maplibregl.Marker({
      element: makePinElement('end'),
      anchor: 'bottom',
    })
      .setLngLat(end as [number, number])
      .addTo(map);

    // Fit to bounds.
    const bounds = new maplibregl.LngLatBounds();
    for (const c of trail.coords) bounds.extend(c as [number, number]);
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 60, duration: 500, maxZoom: 15 });
    }
  }, [trail, mapReady, styleVersion]);

  const handleZoom = useCallback((delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    if (delta > 0) map.zoomIn();
    else map.zoomOut();
  }, []);

  return (
    <div className="absolute inset-0 bg-turbo-900">
      <div ref={containerRef} className="absolute inset-0" />

      {/* Top-Bar */}
      <div className="absolute top-0 left-0 right-0 z-10 safe-top px-3 pt-2 flex items-center gap-2">
        <Link
          to="/navi/custom-trails"
          className="inline-flex items-center gap-1 rounded-full bg-turbo-900/70 backdrop-blur-sm border border-sky-500/30 text-sky-300 text-sm px-3 py-1.5 no-underline"
        >
          <span aria-hidden>←</span> Custom tours
        </Link>
      </div>

      {/* Info-Banner */}
      {trail && (
        <div className="absolute left-3 right-3 top-14 z-10 rounded-xl bg-turbo-900/80 backdrop-blur-sm border border-sky-500/40 p-3 text-white shadow-lg">
          {/* Name-Zeile mit Edit-Button rechts. Wave 14cf-fix15. */}
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-[15px] leading-tight">
                {trail.name}
              </div>
              {/* Difficulty-Badge — visuell konsistent mit Trail-Detail-Sheet. */}
              {trail.difficulty && trail.difficulty !== 'unknown' && (
                <span
                  className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-medium border border-white/20"
                  title={DIFFICULTY_LABEL[trail.difficulty as Difficulty] ?? trail.difficulty}
                >
                  <span
                    className="w-3 h-3 rounded-sm border border-white/40"
                    style={{
                      background: difficultySwatchBackground(
                        trail.difficulty as Difficulty,
                      ),
                    }}
                    aria-hidden="true"
                  />
                  {DIFFICULTY_LABEL[trail.difficulty as Difficulty] ?? trail.difficulty}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="flex-shrink-0 px-2.5 py-1 rounded-md bg-turbo-700/60 hover:bg-turbo-700/85 active:bg-turbo-700 border border-white/15 text-white/85 text-xs font-medium transition-colors"
              aria-label="Edit name and difficulty"
            >
              ✎ Edit
            </button>
          </div>
          <div className="mt-1 text-white/60 text-xs pointer-events-none">
            {formatKm(trail.lengthM)}
            {trail.trailsUsed.length > 0 && ` · ${trail.trailsUsed.length} trails`}
            {' · '}
            {new Date(trail.createdAt).toLocaleDateString()}
          </div>
          {/* Wave 14ca-fix30: Predict-Badge. CustomTrails kennen lengthM aber
              keine elevationGain — wir würden die aus den Coords ableiten,
              aber das ist teuer. Schätzung: 1m_up pro 100m horizontal als
              ballpark (ca. 1% durchschnittliche Steigung in MTB-Touren).
              Wenn der User später echtes Höhenprofil will: aus Coords + SRTM
              berechnen, dann hier als prop. */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <PredictBadge
              distanceKm={trail.lengthM / 1000}
              elevationGainM={Math.round(trail.lengthM / 100)}
            />
            {/* Wave 14ca-fix42 (PRO): GPX-Export der Custom Tour. Inkl.
                cached elevation samples wenn vorhanden (interpoliert pro
                coord). User-Wunsch: "mit höhenprofil" damit auf Bike-
                Computern wie Garmin/Wahoo direkt die Anstiege sichtbar
                sind. */}
            <button
              type="button"
              onClick={async () => {
                const { customTourToGpx, downloadGpx } = await import(
                  '../lib/gpxFormat'
                );
                const gpx = await customTourToGpx(trail);
                downloadGpx(`adt-tour-${trail.name}`, gpx);
              }}
              className="text-xs text-sky-300 hover:text-sky-200 px-2 py-1 rounded border border-sky-500/30 hover:border-sky-500/50 transition-colors"
            >
              ↓ GPX
            </button>
          </div>

          {/* Wave 14cf-fix11: "Auf Karte anzeigen"-Toggle. Bei aktiv wird
              die Tour permanent auf der MapPage gerendert (sky-blue Linie),
              auch ohne Selektion. Mehrere Tours können gleichzeitig
              sichtbar sein. UX-konsistent mit Trails (die per default
              sichtbar sind und über Filter ein-/ausgeblendet werden). */}
          <button
            type="button"
            onClick={async () => {
              const next = !(trail.showOnMap ?? false);
              const updated = await setCustomTrailShowOnMap(trail.id, next);
              if (updated) setTrail(updated);
            }}
            className={`mt-2 w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              trail.showOnMap
                ? 'bg-sky-500/25 border-sky-400/60 text-sky-100 hover:bg-sky-500/35'
                : 'bg-turbo-700/40 border-turbo-500/30 text-white/70 hover:bg-turbo-700/60'
            }`}
            aria-pressed={trail.showOnMap ?? false}
            aria-label="Toggle: show on map"
          >
            <span className="flex items-center gap-2">
              <span aria-hidden="true">{trail.showOnMap ? '👁️' : '👁️‍🗨️'}</span>
              <span>
                {trail.showOnMap ? 'Visible on map' : 'Show on map'}
              </span>
            </span>
            {/* Mini-Toggle-Switch rechts. */}
            <span
              className={`w-9 h-5 rounded-full flex-shrink-0 relative transition-colors ${
                trail.showOnMap ? 'bg-sky-500' : 'bg-white/20'
              }`}
              aria-hidden="true"
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                  trail.showOnMap ? 'left-[18px]' : 'left-0.5'
                }`}
              />
            </span>
          </button>
        </div>
      )}

      {notFound && (
        <div className="absolute left-3 right-3 top-14 z-10 rounded-xl bg-rose-500/20 backdrop-blur-sm border border-rose-400/40 p-3 text-rose-100 text-sm">
          Custom trail not found. It may have been deleted on another device.
        </div>
      )}

      {/* Edit-Modal (Wave 14cf-fix15): Name + Difficulty.
          Live-Apply ist vermieden — wir wollen dass der User explizit auf
          Save tappt, damit ein typo-Slip im Name nicht direkt persistiert
          und der Difficulty-Pick eine bewusste Entscheidung ist. */}
      {editOpen && trail && (
        <EditCustomTrailModal
          trail={trail}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            setTrail(updated);
            setEditOpen(false);
          }}
        />
      )}

      {/* Zoom-Buttons */}
      <div
        className="absolute right-3 z-10 flex flex-col gap-3 pointer-events-none"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4rem)' }}
      >
        <button
          type="button"
          onClick={() => handleZoom(+1)}
          className="pointer-events-auto w-12 h-12 rounded-full bg-turbo-900/65 border border-white/25 backdrop-blur-md shadow-lg flex items-center justify-center text-white text-2xl font-light leading-none active:bg-turbo-900/85"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => handleZoom(-1)}
          className="pointer-events-auto w-12 h-12 rounded-full bg-turbo-900/65 border border-white/25 backdrop-blur-md shadow-lg flex items-center justify-center text-white text-2xl font-light leading-none active:bg-turbo-900/85"
          aria-label="Zoom out"
        >
          −
        </button>
      </div>
    </div>
  );
}

// ─── Edit-Modal: Name + Difficulty ─────────────────────────────────────────
//
// Wave 14cf-fix15. User-Wunsch:
//   "als user sollte ich in der Lage sein, unter custom trails auf edit oder
//    ähnlich bei jeder Tour zu clicken und dann zB den Namen der tour zu
//    ändern. Jeder custom trail sollte die Schwierigkeit vom schwersten
//    beinhalteten trail default haben aber die Schwierigkeit sollte ich als
//    User auch bearbeiten/ändern können. Selbe skala wie trails."
//
// Skala = DIFFICULTY_ORDER (uphill, easy, intermediate, intermediate_plus,
// hard, expert, extreme) plus 'unknown'-Reset. Display via Color-Swatch +
// Label, Klick wählt aus.
function EditCustomTrailModal({
  trail,
  onClose,
  onSaved,
}: {
  trail: CustomTrail;
  onClose: () => void;
  onSaved: (updated: CustomTrail) => void;
}) {
  const [name, setName] = useState(trail.name);
  const [difficulty, setDifficulty] = useState<string>(
    trail.difficulty || 'unknown',
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setErr('Name cannot be empty.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      // Beide Updates seriell — der zweite Call gewinnt für die Cache-
      // Invalidation, beide schreiben aber unabhängig in IDB. Bei Failure
      // im 2. Call ist der Name schon gespeichert; UX-mässig OK, der User
      // kann's nochmal versuchen.
      let updated = trail;
      if (trimmed !== trail.name) {
        const r = await renameCustomTrail(trail.id, trimmed);
        if (r) updated = r;
      }
      if (difficulty !== (trail.difficulty || 'unknown')) {
        const d = await setCustomTrailDifficulty(trail.id, difficulty);
        if (d) updated = d;
      }
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  // 'unknown' als zusätzlicher Eintrag in der Liste (Reset-Option).
  const choices: string[] = ['unknown', ...DIFFICULTY_ORDER];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Edit custom trail"
      onClick={onClose}
    >
      <div
        className="bg-turbo-900 border border-sky-500/30 rounded-2xl p-4 w-full max-w-sm shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-lg font-semibold flex-1">Edit tour</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Name */}
        <label className="block text-xs text-white/60 mb-1" htmlFor="ct-edit-name">
          Name
        </label>
        <input
          id="ct-edit-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-turbo-700/60 border border-white/15 rounded-lg px-3 py-2 text-white text-sm focus:border-sky-400 focus:outline-none mb-4"
          maxLength={120}
          autoFocus
        />

        {/* Difficulty */}
        <div className="text-xs text-white/60 mb-1">Difficulty</div>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          {choices.map((d) => {
            const isOn = difficulty === d;
            const isUnknown = d === 'unknown';
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDifficulty(d)}
                aria-pressed={isOn}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left text-xs transition-colors ${
                  isOn
                    ? 'bg-sky-500/20 border-sky-400/60 text-white'
                    : 'bg-turbo-700/40 border-turbo-700/60 text-white/80 hover:bg-turbo-700/70'
                }`}
              >
                {!isUnknown && (
                  <span
                    className="w-4 h-4 rounded-sm flex-shrink-0 border border-white/20"
                    style={{
                      background: difficultySwatchBackground(d as Difficulty),
                    }}
                    aria-hidden="true"
                  />
                )}
                <span className="truncate">
                  {DIFFICULTY_LABEL[d as Difficulty] ?? d}
                </span>
              </button>
            );
          })}
        </div>

        {err && (
          <div className="text-rose-300 text-xs mb-2 px-1">{err}</div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-3 py-2 rounded-lg bg-turbo-700/60 hover:bg-turbo-700/85 border border-white/15 text-white text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="flex-1 px-3 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 active:bg-sky-600 text-turbo-950 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? '…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function makePinElement(kind: 'start' | 'end'): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText = 'position:relative;width:24px;height:32px;pointer-events:none;';
  const colorMain = kind === 'start' ? '#34d399' : '#fb7185';
  const colorStroke = kind === 'start' ? '#065f46' : '#881337';
  el.innerHTML = `
    <div style="position:absolute;left:2px;top:0;width:20px;height:20px;background:${colorMain};border:2px solid ${colorStroke};border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>
    <div style="position:absolute;left:9px;top:16px;width:0;height:0;border-left:3px solid transparent;border-right:3px solid transparent;border-top:14px solid ${colorStroke};"></div>
  `;
  return el;
}
